import moment from "moment";

const MILESTONES: Record<number, string> = {
  1: "Day one of taking back control",
  2: "You showed up today",
  7: "One week",
  10: "Double digits!",
  14: "Two weeks strong",
  21: "Three weeks",
  30: "One month! Many might quit, but you're still here",
  50: "Unstoppable",
  60: "Two whole months",
  90: "Three months!",
  150: "You refuse to settle",
  180: "Six months",
  250: "Restoration is core to who you are",
  500: "Speechless",
  666: "The devil himself would be proud of your persistence",
  750: "75% of the way to quadruple digits",
  777: "Even the angels admire your dedication",
  911: "Never forget",
};

const MILESTONES_RECURRING: Record<number, string> = {
  50: "Your persistence is relentless",
  75: "Your future self is thanking you",
  90: "Let the countdown begin",
  91: "Something big is coming up soon...",
  92: "Almost at a very round number",
  93: "Slow and steady wins the race",
  94: "The suspense is real",
  95: "So close you can almost taste it",
  96: "The countdown is almost over",
  97: "So close...",
  98: "One away from one away",
  99: "You know what comes next?",
  0: "Legendary", // 100
};

const GENERAL: string[] = [
  "Thanks for showing up today",
  "Consistency is your superpower",
  "Slow and steady wins the race",
  "Another step forward",
  "This is how you reclaim what's yours",
  "Patience and consistency",
  "This is what taking your power back looks like",
  "Another page in your comeback story",
  "Rewriting your story",
  "One step closer",
  "Intention into action",
  "Doing not dreaming",

  // New ones
  "Solid work",
  "You don't miss",
  "Your body, your rules",
  "Staying the course",
  "Defining your own terms",
  "Quiet progress",
  "The long game pays off",
  // "Decide. Act. Repeat.",
  "Investing in your future self",
  "Right on track",
  "Brick by brick",
  "Rome wasn't built in a day",

  // Quotes
  '"Step by step" 🎵', // Whitney or NSYNC
  '"Just keep swimming" 🐠', // Finding Nemo
  '"I can do this all day"', // Captain America
  '"Never tell me the odds"', // Star Wars
  // '"It\'s supposed to be hard. The hard is what makes it great"', // A League of Their Own
  '"I\'m the one at the sail"', // Treasure Planet
  // '"It\'s about how hard you can get hit and keep moving forward"', // Rocky
  '"Make it so"', // Star Trek
  '"You are who you choose to be"', // The Iron Giant
  // '"It is our choices that show what we truly are"', // Harry Potter
  '"Little by little, one travels far"', // JRR Tolkien
  // '"The juice is worth the squeeze"', // Girl Next Door
  '"Whatever it takes"', // Avengers: Endgame
  '"I\'m still here"', // Treasure Planet
  // '"That\'s one small step for man..."', // Neil Armstrong
  '"Today is a good day to try"', // The Hunchback of Notre Dame
  '"The flower that blooms in adversity is the most rare and beautiful of all."', // Mulan
  '"This is the way"', // The Mandalorian
  "\"Don't stop believin'\" 🎵", // Journey
  '"Keep moving forward"', // Meet the Robinsons
  '"Remember who you are" 🦁', // The Lion King
  '"Don\'t stop me now" 🎵', // Queen
  // '"Oh yes, the past can hurt - but you can either run from it, or learn from it"', // Lion King (Rafiki)
];

const YEARS_AS_WORD: Record<number, string> = {
  1: "ONE",
  2: "TWO",
  3: "THREE",
  4: "FOUR",
  5: "FIVE",
  6: "SIX",
  7: "SEVEN",
  8: "EIGHT",
  9: "NINE",
  10: "TEN",
};

/**
 * Get the message for a specific streak day.
 * Priority: specific milestones > yearly anniversaries > recurring milestones > random general message
 */
export function messageFor(day: number): string {
  if (day <= 0) {
    throw new Error("Streak day must be a positive integer");
  }
  // Check specific milestones first
  if (MILESTONES[day]) {
    return MILESTONES[day];
  }

  // Check yearly anniversaries
  if (day % 365 === 0) {
    return "of tugging through every season";
  }

  // Check if streak day matches any of the recurring milestones using modulo
  const recurringKey = day % 100;
  if (MILESTONES_RECURRING[recurringKey] !== undefined) {
    return MILESTONES_RECURRING[recurringKey];
  }

  // Fall back to random general message
  const randomIndex = Math.floor(Math.random() * GENERAL.length);
  return GENERAL[randomIndex];
}

/**
 * Get the full streak message with optional prefix for special days.
 * Used for display in the UI.
 */
export function getStreakMessage(streak: number): string {
  if (streak <= 0) {
    throw new Error("Streak must be a positive integer");
  }
  const isNewYear = moment().format("MM-DD") === "01-01";

  let prefix = "";
  if (streak > 0 && streak % 365 === 0) {
    const yearWord = YEARS_AS_WORD[streak / 365] || (streak / 365).toString();
    prefix = `${yearWord} WHOLE YEAR${streak / 365 > 1 ? "S" : ""}`;
  } else if (streak % 100 === 0) {
    prefix = `${streak.toLocaleString()} DAYS!`;
  }

  return `${prefix} ${
    isNewYear ? "Starting the new year strong!" : messageFor(streak)
  }`.trim();
}

// Export for testing
export const milestonesToTest: number[] = [
  1,
  2,
  7,
  10,
  14,
  21,
  22,
  23,
  24,
  25,
  30,
  50,
  75,
  90,
  91,
  92,
  93,
  94,
  95,
  96,
  97,
  98,
  99,
  100,
  150,
  180,
  190,
  191,
  192,
  193,
  194,
  195,
  196,
  197,
  198,
  199,
  200,
  250,
  365,
  400,
  500,
  365 * 2,
  750,
  1000,
];

// Export constants for testing
export { MILESTONES, MILESTONES_RECURRING, GENERAL };
